import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
  Breakpoint,
  Theme
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

export interface AppModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  width?: Breakpoint
  loading?: boolean
  actions?: React.ReactNode
  showCloseIcon?: boolean
}

interface ModalTheme {
  palette: {
    mode: 'dark' | 'light'
    divider: string
    error: { main: string }
    neutral: { main: string }
  }
  appRadius: {
    lg: string
  }
  shadows: string[]
}

const AppModal: React.FC<AppModalProps> = ({
  open,
  onClose,
  title,
  children,
  width = 'sm',
  loading = false,
  actions,
  showCloseIcon = true
}) => {
  const theme = useTheme() as unknown as ModalTheme
  const isMobile = useMediaQuery((t: Theme) => t.breakpoints.down('sm'))

  const handleClose = (_event: unknown, reason: 'backdropClick' | 'escapeKeyDown'): void => {
    if (loading || reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return
    }
    if (onClose) onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={width}
      fullScreen={isMobile}
      sx={{
        // Target the paper element directly through nested sx selector - avoids PaperProps errors
        '& .MuiDialog-paper': {
          borderRadius: isMobile ? 0 : theme.appRadius.lg,
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          overflow: 'hidden',
          boxShadow: theme.shadows[24]
        }
      }}
    >
      {/* --- HEADER --- */}
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: '600', color: 'text.primary' }}>
          {title}
        </Typography>

        {showCloseIcon && (
          <IconButton
            aria-label="close"
            onClick={loading ? undefined : onClose}
            disabled={loading}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: theme.palette.error.main,
                bgcolor: alpha(theme.palette.error.main, 0.1)
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      {/* --- CONTENT --- */}
      <DialogContent
        dividers
        sx={{
          p: { xs: 2, md: 3 },
          bgcolor: 'background.paper'
        }}
      >
        {children}
      </DialogContent>

      {/* --- FOOTER --- */}
      {actions && (
        <DialogActions
          sx={{
            p: 2,
            bgcolor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.neutral.main, 0.05)
                : 'background.paper',
            borderTop: `1px solid ${theme.palette.divider}`
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  )
}

export default AppModal
